import { isDefined } from "@clipboard-health/util-ts";
import { intersection } from "@src/appV2/lib/utils/set";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useRef } from "react";

import { type HiringFeedPlacement } from "../../HiringFeed/api/hiringFeed.schema";
import { ShiftNameType } from "../../PlacementCandidate/types";
import { useGetPayTransparencyData } from "../api/useGetPayTransparencyData";
import { sortQualificationsByRanking } from "../api/useHiringQualificationRanking";
import { type TransparencyPayRate } from "../types/payRate";
import { computeMinMaxPay, findMatchingPayRates } from "./estimatedPay";

export interface PlacementForComputedPay {
  id: string;
  workplaceId: string;
  workerTypes: HiringFeedPlacement["attributes"]["workerTypes"];
  jobShifts: HiringFeedPlacement["attributes"]["jobShifts"];
  transparencyPayRates: HiringFeedPlacement["attributes"]["transparencyPayRates"];
  attributes: {
    payRange: HiringFeedPlacement["attributes"]["payRange"];
  };
}

interface UsePayTransparencyRates {
  isLoading: boolean;
  getPayRatesForPlacement: (placement: PlacementForComputedPay) => {
    transparencyPayRates: TransparencyPayRate[];
    highestMatchingQualification: string;
    computeMinMaxPay: (
      yearsOfExperience: number | undefined,
      includeBenefits: boolean | undefined
    ) =>
      | {
          min: number;
          max: number;
        }
      | undefined;
  };
  getCachedPayRatesForPlacement: (
    placement: PlacementForComputedPay,
    yearsOfExperienceByQualification: Record<string, number | undefined> | undefined,
    useYearsOfExperienceFallback: boolean,
    includeBenefits: boolean | undefined
  ) => {
    transparencyPayRates: TransparencyPayRate[];
    highestMatchingQualification: string;
    minMaxPayEstimate:
      | {
          min: number;
          max: number;
        }
      | undefined;
  };
  maxResults?: number;
  userYearsOfExperience?: number;
}

export function getYearsOfExperienceOrFallback(yearsOfExperience: number | undefined) {
  if (isDefined(yearsOfExperience)) {
    return yearsOfExperience;
  }

  return 3;
}

function mapExternalTransparencyPayRatesForPlacement(placement: PlacementForComputedPay) {
  return placement.jobShifts.flatMap<TransparencyPayRate>((jobShift) => {
    const baseRates =
      (jobShift.transparencyPayRates?.baseRates?.length ?? 0) > 0
        ? jobShift?.transparencyPayRates?.baseRates
        : placement.transparencyPayRates?.baseRates;

    const { workerTypes, transparencyPayRates: placementTransparencyPayRates } = placement;

    if (!isDefined(baseRates) || !isDefined(workerTypes)) {
      return [];
    }

    const jobShiftDifferentials = jobShift.transparencyPayRates?.differentials;
    const placementDifferentials = placementTransparencyPayRates?.differentials;

    const benefitsDifferential =
      isDefined(jobShiftDifferentials?.benefitsDifferential) ||
      isDefined(jobShiftDifferentials?.healthBenefitsDifferential) ||
      isDefined(jobShiftDifferentials?.ptoBenefitsDifferential)
        ? {
            benefitsDifferential: jobShiftDifferentials?.benefitsDifferential,
            healthBenefitsDifferential: jobShiftDifferentials?.healthBenefitsDifferential,
            ptoBenefitsDifferential: jobShiftDifferentials?.ptoBenefitsDifferential,
          }
        : {
            benefitsDifferential: placementDifferentials?.benefitsDifferential,
            healthBenefitsDifferential: placementDifferentials?.healthBenefitsDifferential,
            ptoBenefitsDifferential: placementDifferentials?.ptoBenefitsDifferential,
          };

    const differentials = {
      ...placementDifferentials,
      ...jobShiftDifferentials,
      // if the job shift has any benefits differential, it will override all the other benefits differentials too
      // because the spread operator respects undefined values (e.g. { a: 1, ...({ a: undefined }) } -> { a: undefined })
      ...benefitsDifferential,
    };

    return baseRates.map<TransparencyPayRate>(({ baseRate, minYearsOfExperience }) => ({
      workerTypes,
      jobTypes: [jobShift.jobType],
      shiftTypes: jobShift.shiftTypes,
      daysOfWeek: jobShift.daysOfWeek,
      baseRate,
      minYearsOfExperience,
      maxYearsOfExperience: 100,
      benefitsDiff: differentials?.benefitsDifferential,
      healthBenefitsDiff: differentials?.healthBenefitsDifferential,
      ptoBenefitsDiff: differentials?.ptoBenefitsDifferential,
      weekendDiff: differentials?.weekendDifferential,
      shiftTypeDiff: {
        [ShiftNameType.AM]: differentials?.dayDifferential,
        [ShiftNameType.PM]: differentials?.eveningDifferential,
        [ShiftNameType.NOC]: differentials?.nightDifferential,
      },
    }));
  });
}

export function usePayTransparencyRates(): UsePayTransparencyRates {
  const { payDataByWorkplace, isLoading: isTransparencyLoading } = useGetPayTransparencyData({
    enabled: true,
  });

  const worker = useDefinedWorker();

  const getPayRatesForPlacement = useCallback(
    (placement: PlacementForComputedPay) => {
      const workerQualifications = new Set(
        worker.licenses.map((license) => license.attributes.qualification)
      );

      const placementQualifications = new Set(placement.workerTypes);

      const matchingQualifications = [
        ...intersection(placementQualifications, workerQualifications),
      ];

      const sortedQualifications = sortQualificationsByRanking(
        matchingQualifications.length > 0 ? matchingQualifications : [...placementQualifications]
      );

      const placementTransparencyPayRates = mapExternalTransparencyPayRatesForPlacement(placement);

      const externalTransparencyPayRates = findMatchingPayRates(
        {
          workplaceId: placement.workplaceId,
          jobShifts: placement.jobShifts,
        },
        payDataByWorkplace[placement.workplaceId]
      );

      const transparencyPayRates =
        placementTransparencyPayRates.length > 0
          ? placementTransparencyPayRates
          : externalTransparencyPayRates;

      // in order to determine which years of experience to use, we need to find the highest qualification that has pay data
      const highestMatchingQualification =
        sortedQualifications.find((qualification) =>
          transparencyPayRates.some((payRate) => payRate.workerTypes.includes(qualification))
        ) ?? sortedQualifications[0];

      const transparencyPayRatesFiltered = transparencyPayRates.filter((payRate) =>
        payRate.workerTypes.includes(highestMatchingQualification)
      );

      const locComputeMinMaxPay = (
        yearsOfExperience: number | undefined,
        includeBenefits: boolean | undefined
      ) =>
        computeMinMaxPay(transparencyPayRatesFiltered, {
          yearsOfExperience,
          includeBenefits,
        });

      return {
        transparencyPayRates: transparencyPayRatesFiltered,
        highestMatchingQualification,
        computeMinMaxPay: locComputeMinMaxPay,
      };
    },
    [payDataByWorkplace, worker.licenses]
  );

  const cachedPayRatesRef = useRef<
    Map<
      string,
      {
        yearsOfExperience: number | undefined;
        transparencyPayRates: TransparencyPayRate[];
        highestMatchingQualification: string;
        minMaxPayEstimate:
          | {
              min: number;
              max: number;
            }
          | undefined;
      }
    >
  >(new Map());

  const getCachedPayRatesForPlacement = (
    placement: PlacementForComputedPay,
    yearsOfExperienceByQualification: Record<string, number | undefined> | undefined,
    useYearsOfExperienceFallback: boolean,
    includeBenefits: boolean | undefined
  ) => {
    if (isTransparencyLoading) {
      return {
        transparencyPayRates: [],
        highestMatchingQualification: "",
        minMaxPayEstimate: undefined,
      };
    }

    const cacheKey = placement.id;

    const cacheEntry = cachedPayRatesRef.current.get(cacheKey);

    if (cacheEntry) {
      const yearsOfExperience = useYearsOfExperienceFallback
        ? getYearsOfExperienceOrFallback(
            yearsOfExperienceByQualification?.[cacheEntry.highestMatchingQualification]
          )
        : yearsOfExperienceByQualification?.[cacheEntry.highestMatchingQualification];

      // invalidate cache if years of experience has changed
      if (yearsOfExperience === cacheEntry.yearsOfExperience) {
        return cacheEntry;
      }
    }

    const { transparencyPayRates, highestMatchingQualification, computeMinMaxPay } =
      getPayRatesForPlacement(placement);

    const yearsOfExperience = useYearsOfExperienceFallback
      ? getYearsOfExperienceOrFallback(
          yearsOfExperienceByQualification?.[highestMatchingQualification]
        )
      : yearsOfExperienceByQualification?.[highestMatchingQualification];

    const minMaxPayEstimate = computeMinMaxPay(yearsOfExperience, includeBenefits);
    cachedPayRatesRef.current.set(cacheKey, {
      yearsOfExperience,
      transparencyPayRates,
      highestMatchingQualification,
      minMaxPayEstimate,
    });

    return { transparencyPayRates, highestMatchingQualification, minMaxPayEstimate };
  };

  return {
    getPayRatesForPlacement,
    getCachedPayRatesForPlacement,
    isLoading: isTransparencyLoading,
  };
}
